import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { catchError, debounceTime, of, startWith, switchMap } from 'rxjs';
import { EligiblePerson, UserService } from '../../services/user.service';

@Component({
  selector: 'app-associate-member-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatAutocompleteModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title>Asociar miembro</h2>
    <mat-dialog-content>
      <p>Seleccioná un miembro sin usuario para vincularlo con {{ data.username }}.</p>
      <mat-form-field appearance="outline" style="width: 100%">
        <mat-label>Buscar miembro</mat-label>
        <input matInput [formControl]="search" [matAutocomplete]="members" />
        <mat-autocomplete #members="matAutocomplete" [displayWith]="displayPerson">
          <mat-option *ngFor="let person of results$ | async" [value]="person">
            {{ displayPerson(person) }} · {{ person.email || person.documentNumber || 'Sin email' }}
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>
      <p *ngIf="error()" role="alert">{{ error() }}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close()" [disabled]="saving()">Cancelar</button>
      <button mat-flat-button (click)="save()" [disabled]="!selectedPersonId || saving()">
        {{ saving() ? 'Asociando…' : 'Asociar miembro' }}
      </button>
    </mat-dialog-actions>
  `
})
export class AssociateMemberDialogComponent {
  readonly search = new FormControl<EligiblePerson | string>('', { nonNullable: true });
  readonly saving = signal(false);
  readonly error = signal('');
  readonly results$;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: { id: string; username: string },
    readonly dialogRef: MatDialogRef<AssociateMemberDialogComponent, boolean>,
    private readonly users: UserService,
    private readonly destroyRef: DestroyRef
  ) {
    this.results$ = this.search.valueChanges.pipe(
      startWith(''), debounceTime(300),
      switchMap(value => {
        this.error.set('');
        if (typeof value !== 'string') return of([]);
        return this.users.getEligiblePeople(value.trim()).pipe(
          switchMap(result => {
            if (!result.items?.length) this.error.set('No se encontraron miembros disponibles.');
            return of(result.items ?? []);
          }),
          catchError(() => { this.error.set('No se pudieron cargar los miembros.'); return of([]); })
        );
      })
    );
  }

  get selectedPersonId(): string | undefined {
    return typeof this.search.value === 'string' ? undefined : this.search.value.id;
  }

  displayPerson(person: EligiblePerson | string): string {
    return typeof person === 'string' ? person : `${person.lastName ?? ''}, ${person.firstName ?? ''}`;
  }

  save(): void {
    if (!this.selectedPersonId || this.saving()) return;
    this.saving.set(true);
    this.dialogRef.disableClose = true;
    this.users.associateMember(this.data.id, this.selectedPersonId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.dialogRef.close(true),
      error: () => {
        this.saving.set(false);
        this.dialogRef.disableClose = false;
        this.error.set('No se pudo asociar el miembro. Puede que ya tenga un usuario vinculado.');
      }
    });
  }
}
